import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { toast } from "sonner";
import {
  ShieldCheck,
  Users,
  ShoppingBag,
  Activity,
  Ticket,
  Plus,
  Ban,
  CheckCircle2,
  RotateCcw,
  Copy,
  Trash2,
  MoreHorizontal,
  Loader2,
  LogOut,
  ImagePlus,
  Sparkles,
} from "lucide-react";

import { useAuth } from "@/auth/AuthProvider";
import { apiFetch } from "@/lib/api";
import { getFirebaseAuth } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// --- Types ---

type Tenant = {
  id: string;
  slug: string;
  name: string;
  legal_name: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  pincode: string;
  gstin: string;
  contact_phone: string;
  contact_email: string;
  currency: string;
  price_per_image_paise: number;
  price_per_video_paise: number | null;
  enabled: number;
  created_at: string;
  updated_at: string;
};

type User = {
  uid: string;
  email: string;
  displayName: string;
  role: "admin" | "imagepackcreator" | "kioskconsumer";
  disabled: boolean;
  lastSignInTime: string;
  creationTime: string;
};

type InviteCode = {
  id: string;
  code: string;
  role: string;
  uses: number;
  max_uses: number | null;
  expires_at: string | null;
  created_at: string;
};

type Order = {
  id: string;
  amount_paise: number;
  currency: string;
  status: string;
  rzp_order_id: string;
  created_at: string;
  // user_email?
};

type AuditRow = {
  id: string;
  actor_uid: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  metadata_json: string;
  created_at: string;
};

type Stats = {
  totalUsers: number;
  totalOrders: number;
  totalRevenuePaise: number;
  totalSessions: number;
};

type ScenePack = {
  id: string;
  title: string;
  description?: string;
  enabled: number;
  created_at: string;
};

type Cameo = {
  id: string;
  name: string;
  description?: string;
  enabled: number;
  created_at: string;
};

// --- Components ---

export function AdminDashboard() {
  const navigate = useNavigate();
  const { claim } = useAuth();
  const isAdmin = claim?.role === "admin";

  const [activeTab, setActiveTab] = useState("scenes");
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  async function handleLogout() {
    try {
      await signOut(getFirebaseAuth());
      navigate("/");
    } catch (err: any) {
      toast.error("Logout failed", { description: err?.message });
    }
  }

  // Initial Data Load
  useEffect(() => {
    async function loadInitial() {
      try {
        setLoading(true);
        if (isAdmin) {
          const [t, s] = await Promise.all([
            apiFetch<{ tenant: Tenant }>("/api/admin/tenant"),
            apiFetch<{ stats: Stats }>("/api/admin/stats"),
          ]);
          setTenant(t.tenant);
          setStats(s.stats);
        } else {
          // For non-admins, just mark as loaded
          setLoading(false);
          return;
        }
      } catch (err: any) {
        toast.error("Failed to load dashboard", { description: err?.message });
      } finally {
        setLoading(false);
      }
    }
    loadInitial();
  }, [isAdmin]);

  if (loading && !tenant && isAdmin) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-950 text-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const dashboardTitle = isAdmin
    ? tenant?.name || "Dashboard"
    : "Creator Dashboard";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 selection:bg-indigo-500/30">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <div className="text-sm font-medium text-slate-400 mb-1">
              {isAdmin ? "Organization Admin" : "Content Creator"}
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-white flex items-center gap-2">
              {isAdmin ? (
                <ShieldCheck className="h-8 w-8 text-indigo-400" />
              ) : (
                <Sparkles className="h-8 w-8 text-indigo-400" />
              )}
              {dashboardTitle}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleLogout}
              className="border-slate-800 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white transition-colors gap-2"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>

        {/* Navigation */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="space-y-6"
        >
          <TabsList className="bg-slate-900 border border-slate-800 p-1 rounded-lg flex-wrap">
            {/* Creator tabs - visible to all */}
            <TabsTrigger
              value="scenes"
              className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-400"
            >
              Scene Packs
            </TabsTrigger>
            <TabsTrigger
              value="cameos"
              className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-400"
            >
              Cameos
            </TabsTrigger>
            {/* Admin-only tabs */}
            {isAdmin && (
              <>
                <TabsTrigger
                  value="overview"
                  className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-400"
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger
                  value="settings"
                  className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-400"
                >
                  Settings
                </TabsTrigger>
                <TabsTrigger
                  value="team"
                  className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-400"
                >
                  Team
                </TabsTrigger>
                <TabsTrigger
                  value="invites"
                  className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-400"
                >
                  Invite Codes
                </TabsTrigger>
                <TabsTrigger
                  value="orders"
                  className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-400"
                >
                  Orders
                </TabsTrigger>
                <TabsTrigger
                  value="audit"
                  className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-400"
                >
                  Audit Log
                </TabsTrigger>
              </>
            )}
          </TabsList>

          <TabsContent
            value="scenes"
            className="space-y-6 animate-in fade-in-50 duration-500 slide-in-from-bottom-2"
          >
            <ScenePackManager />
          </TabsContent>

          <TabsContent
            value="cameos"
            className="space-y-6 animate-in fade-in-50 duration-500 slide-in-from-bottom-2"
          >
            <CameoManager />
          </TabsContent>

          <TabsContent
            value="overview"
            className="space-y-6 animate-in fade-in-50 duration-500 slide-in-from-bottom-2"
          >
            {stats && <OverviewStats stats={stats} />}
          </TabsContent>

          <TabsContent
            value="settings"
            className="space-y-6 animate-in fade-in-50 duration-500 slide-in-from-bottom-2"
          >
            {tenant && <SettingsForm tenant={tenant} onUpdate={setTenant} />}
          </TabsContent>

          <TabsContent
            value="team"
            className="space-y-6 animate-in fade-in-50 duration-500 slide-in-from-bottom-2"
          >
            <TeamManager />
          </TabsContent>

          <TabsContent
            value="invites"
            className="space-y-6 animate-in fade-in-50 duration-500 slide-in-from-bottom-2"
          >
            <InviteManager />
          </TabsContent>

          <TabsContent
            value="orders"
            className="space-y-6 animate-in fade-in-50 duration-500 slide-in-from-bottom-2"
          >
            <OrdersList />
          </TabsContent>

          <TabsContent
            value="audit"
            className="space-y-6 animate-in fade-in-50 duration-500 slide-in-from-bottom-2"
          >
            <AuditLog />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// --- Sub-Components ---

function OverviewStats({ stats }: { stats: Stats }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <StatsCard
        title="Total Revenue"
        value={`₹${(stats.totalRevenuePaise / 100).toLocaleString()}`}
        icon={<ShoppingBag className="h-4 w-4 text-emerald-400" />}
        desc="Gross volume"
      />
      <StatsCard
        title="Active Users"
        value={stats.totalUsers.toString()}
        icon={<Users className="h-4 w-4 text-blue-400" />}
        desc="Team members"
      />
      <StatsCard
        title="Total Orders"
        value={stats.totalOrders.toString()}
        icon={<Ticket className="h-4 w-4 text-purple-400" />}
        desc="Processed transactions"
      />
      <StatsCard
        title="Sessions"
        value={stats.totalSessions.toString()}
        icon={<Activity className="h-4 w-4 text-orange-400" />}
        desc="Kiosk interactions"
      />
    </div>
  );
}

function StatsCard({
  title,
  value,
  icon,
  desc,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  desc: string;
}) {
  return (
    <Card className="bg-slate-900 border-slate-800 text-slate-200 shadow-xl">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-slate-400">
          {title}
        </CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-white">{value}</div>
        <p className="text-xs text-slate-500 mt-1">{desc}</p>
      </CardContent>
    </Card>
  );
}

function SettingsForm({
  tenant,
  onUpdate,
}: {
  tenant: Tenant;
  onUpdate: (t: Tenant) => void;
}) {
  const [formData, setFormData] = useState(tenant);
  const [busy, setBusy] = useState(false);

  // Local state for price inputs to handle decimal cleanly
  const [priceInput, setPriceInput] = useState(
    (tenant.price_per_image_paise / 100).toString(),
  );
  const [videoPriceInput, setVideoPriceInput] = useState(
    tenant.price_per_video_paise
      ? (tenant.price_per_video_paise / 100).toString()
      : "",
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (!isNaN(Number(val)) || val === "") {
      setPriceInput(val);
    }
  };

  const handlePriceBlur = () => {
    const num = Number(priceInput);
    if (isNaN(num) || num < 26) {
      setPriceInput("26");
    }
  };

  const handleVideoPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (!isNaN(Number(val)) || val === "") {
      setVideoPriceInput(val);
    }
  };

  const handleVideoPriceBlur = () => {
    if (videoPriceInput === "") return; // Allow empty (disabled)
    const num = Number(videoPriceInput);
    if (isNaN(num) || num < 501) {
      setVideoPriceInput("501");
    }
  };

  async function save() {
    const pricePaise = Math.round(Number(priceInput) * 100);
    if (pricePaise < 2600) {
      toast.error("Minimum price is ₹26");
      return;
    }

    // Video price: null if empty, otherwise validate minimum
    let videoPricePaise: number | null = null;
    if (videoPriceInput.trim() !== "") {
      videoPricePaise = Math.round(Number(videoPriceInput) * 100);
      if (videoPricePaise < 50100) {
        toast.error("Minimum video price is ₹501");
        return;
      }
    }

    setBusy(true);
    try {
      const payload = {
        ...formData,
        price_per_image_paise: pricePaise,
        price_per_video_paise: videoPricePaise,
      };

      const res = await apiFetch<{ tenant: Tenant }>("/api/admin/tenant", {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });

      onUpdate(res.tenant);
      toast.success("Settings saved successfully");
    } catch (err: any) {
      toast.error("Save failed", { description: err?.message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="bg-slate-900 border-slate-800 text-slate-200">
      <CardHeader>
        <CardTitle className="text-white">Organization Settings</CardTitle>
        <CardDescription className="text-slate-400">
          Manage your organization's details and pricing.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Display Name</Label>
            <Input
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="bg-slate-950 border-slate-800 focus:ring-indigo-500/50"
            />
          </div>
          <div className="space-y-2">
            <Label>Legal Name</Label>
            <Input
              name="legal_name"
              value={formData.legal_name || ""}
              onChange={handleChange}
              className="bg-slate-950 border-slate-800 focus:ring-indigo-500/50"
            />
          </div>
          <div className="space-y-2">
            <Label>Contact Email</Label>
            <Input
              name="contact_email"
              value={formData.contact_email || ""}
              onChange={handleChange}
              className="bg-slate-950 border-slate-800 focus:ring-indigo-500/50"
            />
          </div>
          <div className="space-y-2">
            <Label>Contact Phone</Label>
            <Input
              name="contact_phone"
              value={formData.contact_phone || ""}
              onChange={handleChange}
              className="bg-slate-950 border-slate-800 focus:ring-indigo-500/50"
            />
          </div>
          <div className="space-y-2">
            <Label>GSTIN</Label>
            <Input
              name="gstin"
              value={formData.gstin || ""}
              onChange={handleChange}
              className="bg-slate-950 border-slate-800 focus:ring-indigo-500/50"
            />
          </div>
          <div className="space-y-2">
            <Label>Price Per Image (INR, min ₹26)</Label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-slate-500">₹</span>
              <Input
                value={priceInput}
                onChange={handlePriceChange}
                onBlur={handlePriceBlur}
                className="pl-8 bg-slate-950 border-slate-800 focus:ring-indigo-500/50"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>
              Price Per Video (INR, min ₹501, leave empty to disable)
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-slate-500">₹</span>
              <Input
                value={videoPriceInput}
                onChange={handleVideoPriceChange}
                onBlur={handleVideoPriceBlur}
                placeholder="501"
                className="pl-8 bg-slate-950 border-slate-800 focus:ring-indigo-500/50"
              />
            </div>
          </div>
        </div>

        <div className="space-y-2 pt-4 border-t border-slate-800">
          <Label className="text-base font-semibold text-white">Address</Label>
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              placeholder="Line 1"
              name="address_line_1"
              value={formData.address_line_1 || ""}
              onChange={handleChange}
              className="bg-slate-950 border-slate-800"
            />
            <Input
              placeholder="Line 2"
              name="address_line_2"
              value={formData.address_line_2 || ""}
              onChange={handleChange}
              className="bg-slate-950 border-slate-800"
            />
            <Input
              placeholder="City"
              name="city"
              value={formData.city || ""}
              onChange={handleChange}
              className="bg-slate-950 border-slate-800"
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                placeholder="State"
                name="state"
                value={formData.state || ""}
                onChange={handleChange}
                className="bg-slate-950 border-slate-800"
              />
              <Input
                placeholder="Pincode"
                name="pincode"
                value={formData.pincode || ""}
                onChange={handleChange}
                className="bg-slate-950 border-slate-800"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <Button
            onClick={save}
            disabled={busy}
            className="bg-indigo-600 hover:bg-indigo-700 text-white min-w-[100px]"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function TeamManager() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch<{ users: User[] }>("/api/admin/users");
      setUsers(res.users);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleUserCreated = () => {
    load();
  };

  return (
    <Card className="bg-slate-900 border-slate-800 text-slate-200">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-white">Team Members</CardTitle>
          <CardDescription className="text-slate-400">
            Manage access to your organization.
          </CardDescription>
        </div>
        <CreateUserDialog onCreated={handleUserCreated} />
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 text-center text-slate-500">Loading team...</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-slate-400">User</TableHead>
                <TableHead className="text-slate-400">Role</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400 text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <UserRow key={u.uid} user={u} onUpdate={load} />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function UserRow({ user, onUpdate }: { user: User; onUpdate: () => void }) {
  const [busy, setBusy] = useState(false);

  const toggleStatus = async () => {
    if (
      !confirm(
        `Are you sure you want to ${user.disabled ? "enable" : "disable"} this user?`,
      )
    )
      return;
    setBusy(true);
    try {
      await apiFetch(`/api/admin/users/${user.uid}/disable`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ disabled: !user.disabled }),
      });
      toast.success("User updated");
      onUpdate();
    } catch (err: any) {
      toast.error("Action failed", { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <TableRow className="border-slate-800 hover:bg-slate-800/50">
      <TableCell>
        <div className="flex flex-col">
          <span className="font-medium text-slate-200">
            {user.displayName || "No Name"}
          </span>
          <span className="text-xs text-slate-500">{user.email}</span>
        </div>
      </TableCell>
      <TableCell>
        <span className="inline-flex items-center rounded-md bg-slate-800 px-2 py-1 text-xs font-medium text-indigo-400 ring-1 ring-inset ring-indigo-400/30">
          {user.role}
        </span>
      </TableCell>
      <TableCell>
        {user.disabled ? (
          <span className="text-red-400 flex items-center gap-1 text-xs">
            <Ban className="h-3 w-3" /> Disabled
          </span>
        ) : (
          <span className="text-emerald-400 flex items-center gap-1 text-xs">
            <CheckCircle2 className="h-3 w-3" /> Active
          </span>
        )}
      </TableCell>
      <TableCell className="text-right">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleStatus}
          disabled={busy}
          className="h-8 w-8 p-0 text-slate-400 hover:text-white"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function CreateUserDialog({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState({
    email: "",
    password: "",
    displayName: "",
    role: "kioskconsumer",
  });

  async function submit() {
    setBusy(true);
    try {
      await apiFetch("/api/admin/users", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(data),
      });
      toast.success("User created successfully");
      setOpen(false);
      setData({
        email: "",
        password: "",
        displayName: "",
        role: "kioskconsumer",
      });
      onCreated();
    } catch (err: any) {
      toast.error("Failed to create user", { description: err?.message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2">
          <Plus className="h-4 w-4" /> Add User
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
        <DialogHeader>
          <DialogTitle>Add Team Member</DialogTitle>
          <DialogDescription className="text-slate-400">
            Create a new user account for your organization.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              value={data.email}
              onChange={(e) => setData({ ...data, email: e.target.value })}
              className="bg-slate-950 border-slate-800"
            />
          </div>
          <div className="space-y-2">
            <Label>Password</Label>
            <Input
              type="password"
              value={data.password}
              onChange={(e) => setData({ ...data, password: e.target.value })}
              className="bg-slate-950 border-slate-800"
              placeholder="Min 12 characters"
            />
          </div>
          <div className="space-y-2">
            <Label>Display Name</Label>
            <Input
              value={data.displayName}
              onChange={(e) =>
                setData({ ...data, displayName: e.target.value })
              }
              className="bg-slate-950 border-slate-800"
            />
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <select
              className="w-full h-10 rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              value={data.role}
              onChange={(e) => setData({ ...data, role: e.target.value })}
            >
              <option value="kioskconsumer">Kiosk Consumer (Device)</option>
              <option value="imagepackcreator">Image Pack Creator</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || data.password.length < 12}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {busy ? "Creating..." : "Create User"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteManager() {
  const [codes, setCodes] = useState<InviteCode[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch<{ inviteCodes: InviteCode[] }>(
        "/api/admin/invite-codes",
      );
      setCodes(res.inviteCodes);
    } catch (err) {
      toast.error("Failed to load invite codes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const revoke = async (id: string) => {
    if (!confirm("Revoke this invite code?")) return;
    try {
      await apiFetch(`/api/admin/invite-codes/${id}`, { method: "DELETE" });
      toast.success("Code revoked");
      load();
    } catch (err: any) {
      toast.error("Failed to revoke", { description: err?.message });
    }
  };

  return (
    <Card className="bg-slate-900 border-slate-800 text-slate-200">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-white">Invite Codes</CardTitle>
          <CardDescription className="text-slate-400">
            Generate codes for easy onboarding.
          </CardDescription>
        </div>
        <CreateInviteDialog onCreated={load} />
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 text-center text-slate-500">
            Loading codes...
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-slate-400">Code</TableHead>
                <TableHead className="text-slate-400">Role</TableHead>
                <TableHead className="text-slate-400">Usage</TableHead>
                <TableHead className="text-slate-400">Expires</TableHead>
                <TableHead className="text-slate-400 text-right">
                  Action
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {codes.map((c) => (
                <TableRow
                  key={c.code}
                  className="border-slate-800 hover:bg-slate-800/50"
                >
                  <TableCell className="font-mono text-indigo-300 font-medium">
                    {c.code}
                  </TableCell>
                  <TableCell className="text-slate-300">{c.role}</TableCell>
                  <TableCell className="text-slate-400">
                    {c.uses} / {c.max_uses || "∞"}
                  </TableCell>
                  <TableCell className="text-slate-400">
                    {c.expires_at
                      ? new Date(c.expires_at).toLocaleDateString()
                      : "Never"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => revoke(c.id || c.code)}
                      className="h-8 w-8 p-0 text-slate-500 hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {codes.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-8 text-slate-500"
                  >
                    No active invite codes
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function CreateInviteDialog({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    role: "kioskconsumer",
    maxUses: "1",
    expiresInDays: "7",
  });
  const [lastCode, setLastCode] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    try {
      const body = {
        role: form.role,
        maxUses: form.maxUses ? parseInt(form.maxUses) : null,
        expiresInDays: form.expiresInDays ? parseInt(form.expiresInDays) : null,
      };

      const res = await apiFetch<{ code: string }>("/api/admin/invite-codes", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });

      setLastCode(res.code);
      toast.success("Invite code generated");
      onCreated();
    } catch (err: any) {
      toast.error("Failed to generate", { description: err?.message });
    } finally {
      setBusy(false);
    }
  }

  const handleClose = () => {
    setOpen(false);
    setLastCode(null);
    setForm({ role: "kioskconsumer", maxUses: "1", expiresInDays: "7" });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2">
          <Ticket className="h-4 w-4" /> New Code
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
        <DialogHeader>
          <DialogTitle>Generate Invite Code</DialogTitle>
        </DialogHeader>

        {lastCode ? (
          <div className="py-6 flex flex-col items-center gap-4 animate-in fade-in zoom-in-95">
            <div className="text-sm text-slate-400">
              Share this code with your user:
            </div>
            <div className="text-4xl font-mono font-bold text-white tracking-widest bg-slate-950 px-6 py-4 rounded-xl border border-indigo-500/30 shadow-[0_0_30px_-10px_rgba(99,102,241,0.3)]">
              {lastCode}
            </div>
            <Button
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(lastCode);
                toast.success("Copied!");
              }}
              className="gap-2 border-slate-700 hover:bg-slate-800 text-slate-300"
            >
              <Copy className="h-4 w-4" /> Copy Code
            </Button>
            <Button
              className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={handleClose}
            >
              Done
            </Button>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Role</Label>
              <select
                className="w-full h-10 rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-200"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                <option value="kioskconsumer">Kiosk Consumer (Device)</option>
                <option value="imagepackcreator">Image Pack Creator</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max Uses</Label>
                <Input
                  type="number"
                  value={form.maxUses}
                  onChange={(e) =>
                    setForm({ ...form, maxUses: e.target.value })
                  }
                  className="bg-slate-950 border-slate-800"
                />
              </div>
              <div className="space-y-2">
                <Label>Expires (Days)</Label>
                <Input
                  type="number"
                  value={form.expiresInDays}
                  onChange={(e) =>
                    setForm({ ...form, expiresInDays: e.target.value })
                  }
                  className="bg-slate-950 border-slate-800"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={submit}
                disabled={busy}
                className="bg-indigo-600 hover:bg-indigo-700 text-white w-full"
              >
                {busy ? "Generating..." : "Generate Code"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OrdersList() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch<{ orders: Order[] }>("/api/admin/orders");
      setOrders(res.orders);
    } catch (err) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const refund = async (id: string) => {
    const reason = prompt("Enter refund reason:");
    if (!reason) return;

    try {
      await apiFetch(`/api/admin/orders/${id}/refund`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ reason }),
      });
      toast.success("Refund processed");
      load();
    } catch (err: any) {
      toast.error("Refund failed", { description: err?.message });
    }
  };

  return (
    <Card className="bg-slate-900 border-slate-800 text-slate-200">
      <CardHeader>
        <CardTitle className="text-white">Recent Orders</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 text-center text-slate-500">
            Loading orders...
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-slate-400">Date</TableHead>
                <TableHead className="text-slate-400">Amount</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Order ID</TableHead>
                <TableHead className="text-slate-400 text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow
                  key={o.id}
                  className="border-slate-800 hover:bg-slate-800/50"
                >
                  <TableCell className="text-slate-400 text-xs">
                    {new Date(o.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="font-medium text-slate-200">
                    {o.currency} {(o.amount_paise / 100).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        o.status === "paid"
                          ? "bg-emerald-950 text-emerald-400 border border-emerald-900"
                          : o.status === "refunded"
                            ? "bg-orange-950 text-orange-400 border border-orange-900"
                            : "bg-slate-800 text-slate-400"
                      }`}
                    >
                      {o.status}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">
                    {o.rzp_order_id}
                  </TableCell>
                  <TableCell className="text-right">
                    {o.status !== "refunded" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => refund(o.id)}
                        className="h-7 text-xs text-orange-400 hover:text-orange-300 hover:bg-orange-950/30"
                      >
                        <RotateCcw className="h-3 w-3 mr-1" /> Refund
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function AuditLog() {
  const [logs, setLogs] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await apiFetch<{ audit: AuditRow[] }>("/api/admin/audit");
        setLogs(res.audit);
      } catch (err) {
        // silent fail
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, []);

  return (
    <Card className="bg-slate-900 border-slate-800 text-slate-200">
      <CardHeader>
        <CardTitle className="text-white">Audit Log</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 text-center text-slate-500">
            Loading audit log...
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-slate-400">Time</TableHead>
                <TableHead className="text-slate-400">Action</TableHead>
                <TableHead className="text-slate-400">Actor</TableHead>
                <TableHead className="text-slate-400">Target</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <TableRow
                  key={log.id}
                  className="border-slate-800 hover:bg-slate-800/50"
                >
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-sm font-medium text-slate-300">
                    {log.action}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 font-mono">
                    {log.actor_uid.substring(0, 8)}...
                  </TableCell>
                  <TableCell className="text-xs text-slate-400">
                    {log.target_type}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Creator Components (Scene Packs & Cameos)
// ─────────────────────────────────────────────────────────────

const WEBP_QUALITY = 0.7; // Fixed 70% quality

async function imageToWebp(file: File, quality: number): Promise<Blob> {
  const img = document.createElement("img");
  img.src = URL.createObjectURL(file);
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("image_load_failed"));
  });

  const size = Math.min(img.naturalWidth, img.naturalHeight);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas_unavailable");
  const sx = (img.naturalWidth - size) / 2;
  const sy = (img.naturalHeight - size) / 2;
  ctx.drawImage(img, sx, sy, size, size, 0, 0, size, size);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("webp_failed"))),
      "image/webp",
      quality,
    );
  });
  URL.revokeObjectURL(img.src);
  return blob;
}

function ScenePackManager() {
  const [packs, setPacks] = useState<ScenePack[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch<{ scenePacks: ScenePack[] }>(
        "/api/creator/scene-packs",
      );
      setPacks(res.scenePacks);
    } catch (err: any) {
      toast.error("Failed to load scene packs", { description: err?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <Card className="bg-slate-900 border-slate-800 text-slate-200">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-white">Scene Packs</CardTitle>
          <CardDescription className="text-slate-400">
            Background scenes for AI image generation.
          </CardDescription>
        </div>
        <CreateScenePackDialog onCreated={load} />
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 text-center text-slate-500">
            Loading scene packs...
          </div>
        ) : packs.length === 0 ? (
          <div className="py-8 text-center text-slate-500">
            No scene packs yet. Create your first one!
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {packs.map((p) => (
              <div
                key={p.id}
                className="overflow-hidden rounded-xl border border-slate-800 bg-slate-950"
              >
                <div className="aspect-square bg-slate-800">
                  <img
                    src={`/api/public/scene-packs/${p.id}/image`}
                    className="h-full w-full object-cover"
                    alt={p.title}
                  />
                </div>
                <div className="p-4">
                  <div className="font-semibold text-white">{p.title}</div>
                  <div className="mt-1 line-clamp-2 text-sm text-slate-400">
                    {p.description || "No description"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CreateScenePackDialog({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!title || !file) return;
    setBusy(true);
    try {
      const webp = await imageToWebp(file, WEBP_QUALITY);
      const created = await apiFetch<{ id: string }>(
        "/api/creator/scene-packs",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ title, description }),
        },
      );

      const form = new FormData();
      form.append(
        "file",
        new File([webp], "scene.webp", { type: "image/webp" }),
      );
      await apiFetch(`/api/creator/scene-packs/${created.id}/image`, {
        method: "PUT",
        body: form,
      });

      toast.success("Scene pack created");
      setOpen(false);
      setTitle("");
      setDescription("");
      setFile(null);
      onCreated();
    } catch (err: any) {
      toast.error("Create failed", { description: err?.message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2">
          <ImagePlus className="h-4 w-4" /> New Scene Pack
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
        <DialogHeader>
          <DialogTitle>Create Scene Pack</DialogTitle>
          <DialogDescription className="text-slate-400">
            Upload a background scene for AI image generation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Beach Sunset"
              className="bg-slate-950 border-slate-800"
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description..."
              className="bg-slate-950 border-slate-800"
            />
          </div>
          <div className="space-y-2">
            <Label>Image</Label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="bg-slate-950 border-slate-800"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !title || !file}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CameoManager() {
  const [cameos, setCameos] = useState<Cameo[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch<{ cameos: Cameo[] }>("/api/creator/cameos");
      setCameos(res.cameos);
    } catch (err: any) {
      toast.error("Failed to load cameos", { description: err?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <Card className="bg-slate-900 border-slate-800 text-slate-200">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-white">Cameos</CardTitle>
          <CardDescription className="text-slate-400">
            Character cameos for AI image generation.
          </CardDescription>
        </div>
        <CreateCameoDialog onCreated={load} />
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 text-center text-slate-500">
            Loading cameos...
          </div>
        ) : cameos.length === 0 ? (
          <div className="py-8 text-center text-slate-500">
            No cameos yet. Create your first one!
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {cameos.map((c) => (
              <div
                key={c.id}
                className="overflow-hidden rounded-xl border border-slate-800 bg-slate-950"
              >
                <div className="aspect-square bg-slate-800">
                  <img
                    src={`/api/public/cameos/${c.id}/image`}
                    className="h-full w-full object-cover"
                    alt={c.name}
                  />
                </div>
                <div className="p-4">
                  <div className="font-semibold text-white">{c.name}</div>
                  <div className="mt-1 line-clamp-2 text-sm text-slate-400">
                    {c.description || "No description"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CreateCameoDialog({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [snippet, setSnippet] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!name || !file) return;
    setBusy(true);
    try {
      const webp = await imageToWebp(file, WEBP_QUALITY);
      const created = await apiFetch<{ id: string }>("/api/creator/cameos", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name,
          description,
          cameoPromptSnippet: snippet,
        }),
      });

      const form = new FormData();
      form.append(
        "file",
        new File([webp], "cameo.webp", { type: "image/webp" }),
      );
      await apiFetch(`/api/creator/cameos/${created.id}/image`, {
        method: "PUT",
        body: form,
      });

      toast.success("Cameo created");
      setOpen(false);
      setName("");
      setDescription("");
      setSnippet("");
      setFile(null);
      onCreated();
    } catch (err: any) {
      toast.error("Create failed", { description: err?.message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2">
          <Sparkles className="h-4 w-4" /> New Cameo
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
        <DialogHeader>
          <DialogTitle>Create Cameo</DialogTitle>
          <DialogDescription className="text-slate-400">
            Add a character overlay for AI image generation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Pirate Captain"
              className="bg-slate-950 border-slate-800"
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description..."
              className="bg-slate-950 border-slate-800"
            />
          </div>
          <div className="space-y-2">
            <Label>Prompt Snippet</Label>
            <Input
              value={snippet}
              onChange={(e) => setSnippet(e.target.value)}
              placeholder="e.g., wearing a pirate hat and eyepatch"
              className="bg-slate-950 border-slate-800"
            />
          </div>
          <div className="space-y-2">
            <Label>Image</Label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="bg-slate-950 border-slate-800"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !name || !file}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
